/**
 * AgendaEditor — M-Rhythm-1 (F-MUT1 Packet B AC-13 redesign)
 *
 * Compact row design with:
 *   - Drag-handle (GripVertical) for reordering via HTML5 drag-and-drop
 *   - Inline title input
 *   - Accountable picker per item (template: any actor; instance: scoped to participants)
 *   - Duration stepper with "min" suffix
 *   - Per-row delete (Trash2)
 *   - Ghost "+ Add topic" row at the bottom
 *   - Live "Total: X min" footer
 *
 * Two modes (via `mode` prop):
 *   - 'template'  — full accountable picker, used in Settings → Cadence → Agenda section.
 *   - 'instance'  — accountable restricted to `participantActors`, used in MeetingDetailModal.
 */

import React from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input/Input';
import { NumberInput } from '@/components/ui/NumberInput';
import { GripVertical, Trash2, Plus } from 'lucide-react';
import { AccountablePicker } from './AccountablePicker';
import { t } from '@/lib/i18n';
import type { RorosActorDTO } from '@/types/roros/actors';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface AgendaItemDraft {
  /** Client-side stable key (not the DB id). */
  key: string;
  title: string;
  accountableActorId: string | null;
  durationMinutes: number | null;
}

export interface AgendaEditorProps {
  /** Current list of agenda items. */
  items: AgendaItemDraft[];
  onChange: (items: AgendaItemDraft[]) => void;
  /**
   * 'template'  — no participant restriction; any actor can be chosen.
   * 'instance'  — accountable picker is scoped to `participantActors`.
   * @default 'template'
   */
  mode?: 'template' | 'instance';
  /** All actors to display in actor-only picker (template mode). */
  actors?: RorosActorDTO[];
  /** Participant subset (instance mode). */
  participantActors?: RorosActorDTO[];
  disabled?: boolean;
  /** Maximum number of items allowed (optional). */
  maxItems?: number;
  'data-testid'?: string;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

let _keyCounter = 0;
function nextKey(): string {
  return `agenda-${++_keyCounter}`;
}

const DURATION_STEP = 5;

function clampDuration(val: number): number {
  return Math.max(0, Math.round(val / DURATION_STEP) * DURATION_STEP);
}

// ─── Component ─────────────────────────────────────────────────────────────

export function AgendaEditor({
  items,
  onChange,
  mode = 'template',
  actors = [],
  participantActors = [],
  disabled = false,
  maxItems,
  'data-testid': testId,
}: AgendaEditorProps): React.JSX.Element {
  const effectiveActors = mode === 'instance' ? participantActors : actors;

  const [dragIndex, setDragIndex] = React.useState<number | null>(null);

  const update = (index: number, partial: Partial<AgendaItemDraft>) => {
    onChange(items.map((item, i) => (i === index ? { ...item, ...partial } : item)));
  };

  const remove = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const addItem = () => {
    onChange([...items, { key: nextKey(), title: '', accountableActorId: null, durationMinutes: null }]);
  };

  const canAdd = !maxItems || items.length < maxItems;

  const totalMinutes = items.reduce((sum, item) => sum + (item.durationMinutes ?? 0), 0);

  return (
    <div className="space-y-1.5" data-testid={testId}>
      {items.map((item, index) => (
        <div
          key={item.key}
          className="flex items-center gap-2 bg-background border border-border rounded-md px-2 py-1.5"
          data-testid={testId ? `${testId}-item-${index}` : undefined}
          draggable={!disabled}
          onDragStart={() => setDragIndex(index)}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => {
            e.preventDefault();
            if (dragIndex !== null && dragIndex !== index) {
              const next = [...items];
              const [moved] = next.splice(dragIndex, 1);
              next.splice(index, 0, moved);
              onChange(next);
            }
            setDragIndex(null);
          }}
          onDragEnd={() => setDragIndex(null)}
        >
          {/* Drag handle */}
          <span
            className="shrink-0 text-muted-foreground cursor-grab active:cursor-grabbing"
            aria-hidden="true"
            // Hidden move-up kept for backward compat with test selectors
            data-testid={testId ? `${testId}-item-${index}-move-up` : undefined}
          >
            <GripVertical className="h-4 w-4" />
          </span>

          {/* Title */}
          <div className="flex-1 min-w-0">
            <Input
              type="text"
              value={item.title}
              onChange={(e) => update(index, { title: e.target.value })}
              disabled={disabled}
              placeholder={t('Agenda item title')}
              aria-label={t('Agenda item title')}
              className="w-full text-sm bg-transparent border-0 border-b border-border focus:border-primary py-0.5 placeholder:text-muted-foreground h-7"
              data-testid={testId ? `${testId}-item-${index}-title` : undefined}
            />
          </div>

          {/* Accountable */}
          {effectiveActors.length > 0 && (
            <div className="shrink-0">
              <AccountablePicker
                value={{ actorId: item.accountableActorId, domainId: null }}
                onChange={({ actorId }) => update(index, { accountableActorId: actorId })}
                actors={effectiveActors}
                mode="actor-only"
                disabled={disabled}
                placeholder={t('Accountable')}
                className="w-36"
                data-testid={testId ? `${testId}-item-${index}-accountable` : undefined}
              />
            </div>
          )}

          {/* Duration stepper + "min" suffix */}
          <div className="flex items-center gap-1 shrink-0">
            <NumberInput
              value={item.durationMinutes}
              onChange={(v) => update(index, { durationMinutes: v === null ? null : clampDuration(v) })}
              min={0}
              max={600}
              step={DURATION_STEP}
              disabled={disabled}
              placeholder="—"
              compact
              data-testid={testId ? `${testId}-item-${index}-duration` : undefined}
            />
            <span className="text-xs text-muted-foreground">{t('min')}</span>
          </div>

          {/* Remove */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 shrink-0 text-muted-foreground hover:text-destructive"
            disabled={disabled}
            onClick={() => remove(index)}
            aria-label={t('Remove agenda item')}
            data-testid={testId ? `${testId}-item-${index}-remove` : undefined}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      ))}

      {/* Ghost "+ Add topic" row */}
      {canAdd && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground border border-dashed border-border rounded-md hover:border-primary hover:text-primary transition-colors justify-start h-auto"
          disabled={disabled}
          onClick={addItem}
          data-testid={testId ? `${testId}-add` : undefined}
        >
          <Plus className="h-3.5 w-3.5" />
          {t('Add topic')}
        </Button>
      )}

      {/* Live total footer */}
      <div className="flex justify-end text-xs text-muted-foreground pt-1">
        {t('Total')}: {totalMinutes} {t('min')}
      </div>
    </div>
  );
}
